import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/* ======================= Helpers ======================= */

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  if (a === 0 && b === 0) return 1;
  while (b !== 0) {
    const t = b;
    b = a % b;
    a = t;
  }
  return a === 0 ? 1 : a;
};

const simplifyFraction = (num, den) => {
  const g = gcd(num, den);
  return {
    numerator: num / g,
    denominator: den / g,
  };
};

const fractionToLatex = ({ numerator, denominator }) => {
  if (!denominator || denominator === 0) return "";
  if (denominator === 1) return `${numerator}`;
  return `\\dfrac{${numerator}}{${denominator}}`;
};

const isValidFractionObject = (f) =>
  f &&
  typeof f === "object" &&
  f.numerator !== undefined &&
  f.denominator !== undefined;

/** parsowanie ułamka zwykłego / mieszanego z stringa */
const parseFractionFromString = (input) => {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  let sign = 1;
  let rest = trimmed;
  if (rest[0] === "-" || rest[0] === "+") {
    if (rest[0] === "-") sign = -1;
    rest = rest.slice(1).trim();
  }

  let whole = 0;
  let num;
  let den;

  const spaceParts = rest.split(/\s+/);

  if (spaceParts.length === 2) {
    // zapis mieszany: "2 3/5"
    whole = parseInt(spaceParts[0], 10);
    if (!Number.isFinite(whole)) return null;

    const fracParts = spaceParts[1].split("/");
    if (fracParts.length !== 2) return null;

    num = parseInt(fracParts[0], 10);
    den = parseInt(fracParts[1], 10);
    if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;

    num = sign * (whole * den + num);
  } else {
    // sam ułamek "3/5" albo liczba całkowita "4"
    const fracParts = rest.split("/");
    if (fracParts.length === 1) {
      num = sign * parseInt(fracParts[0], 10);
      den = 1;
      if (!Number.isFinite(num)) return null;
    } else if (fracParts.length === 2) {
      num = sign * parseInt(fracParts[0], 10);
      den = parseInt(fracParts[1], 10);
      if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
    } else {
      return null;
    }
  }

  return simplifyFraction(num, den);
};

/** parsowanie liczby dziesiętnej (kropka albo przecinek) */
const parseDecimalFromString = (input) => {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(",", ".");
  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  return value;
};

/* ======================= GENERATOR ======================= */
/**
 * Zadanie: cenę towaru podniesiono/obniżono o p% i teraz wynosi ona K zł.
 * Oblicz, ile kosztował towar przed zmianą ceny.
 *
 *  - p – wielokrotność 5 (5–50)
 *  - cena przed i po zmianie to liczby całkowite (zł)
 */
export function generateTask() {
  const isIncrease = Math.random() < 0.5;

  const percents = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50];
  const percent = percents[randInt(0, percents.length - 1)];

  // baza jak w zadaniu "do przodu", żeby mieć ładne liczby
  // newPrice = base * (100 ± p) / 100 jest całkowite
  const factor = 100 + (isIncrease ? percent : -percent); // np. 120 albo 80
  const step = 100 / gcd(100, factor);
  const base = step * randInt(3, 10); // cena PRZED zmianą
  const finalPrice = (base * factor) / 100; // cena PO zmianie – całkowita

  return {
    type: "PercentChangeReverseSingleTask",
    data: {
      base,       // szukana cena przed zmianą
      percent,    // o ile procent zmieniono cenę
      isIncrease, // true = podwyżka, false = obniżka
      finalPrice, // dana w treści cena po zmianie
    },
  };
}

/* ======================= RENDER TASK ======================= */

export function renderTask({ data }) {
  const { percent, isIncrease, finalPrice } = data;
  const verb = isIncrease ? "podniesiono" : "obniżono";

  return (
    <p>
      Cenę pewnego towaru {verb} o{" "}
      <InlineMath math={`${percent}\\%`} /> i po tej zmianie wynosi ona{" "}
      <InlineMath math={`${finalPrice}\\,\\text{zł}`} />. Oblicz, ile ten
      towar kosztował przed tą zmianą ceny.
    </p>
  );
}

/* ======================= INPUT ======================= */

export function renderInput(_, userAnswer, onChange, disabled) {
  const handleChange = (ans) => onChange?.(ans);

  return (
    <div style={{ marginTop: "0.75rem" }}>
      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        placeholder="np. 80, 45/2, 22.5"
        label=""
        suffixLatex="\text{zł}"
      />
    </div>
  );
}

/* ======================= CHECK ======================= */

export function checkAnswer({ data }, userAnswer) {
  const expected = data.base; // szukamy ceny sprzed zmiany

  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return {
      isCorrect: false,
      expected,
      actual: null,
      comment: null,
    };
  }

  const raw = String(userAnswer.raw).trim();

  let value = null;

  // 1) spróbuj jako ułamek
  const frac = parseFractionFromString(raw);
  if (frac && isValidFractionObject(frac)) {
    if (frac.denominator !== 0) {
      value = frac.numerator / frac.denominator;
    }
  } else {
    // 2) spróbuj jako liczba dziesiętna
    value = parseDecimalFromString(raw);
  }

  if (value === null) {
    return {
      isCorrect: false,
      expected,
      actual: null,
      comment: null,
    };
  }

  const isCorrect = Math.abs(value - expected) <= 0.005; // tolerancja do groszy

  return {
    isCorrect,
    expected,
    actual: value,
    comment: null,
  };
}

/* ======================= ANSWER & USER ANSWER ======================= */

export function renderAnswer({ data }) {
  const { base } = data;

  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <InlineMath math={`${base}\\,\\text{zł}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer !== "object" ||
    !userAnswer.raw ||
    !String(userAnswer.raw).trim()
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const raw = String(userAnswer.raw).trim();

  const parsedFrac = parseFractionFromString(raw);
  if (parsedFrac) {
    const latex = fractionToLatex(parsedFrac);
    return (
      <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
        <InlineMath math={`${latex}\\,\\text{zł}`} />
      </div>
    );
  }

  const decimalVal = parseDecimalFromString(raw);
  if (decimalVal !== null) {
    const normalized = raw.replace(",", ".");
    return (
      <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
        <InlineMath math={`${normalized}\\,\\text{zł}`} />
      </div>
    );
  }

  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <InlineMath math={`${raw}\\,\\text{zł}`} />
    </div>
  );
}

/* ======================= EXPLANATION ======================= */

export function renderExplanation({ data }) {
  const { base, percent, isIncrease, finalPrice } = data;

  const sign = isIncrease ? "+" : "-";
  const factorNum = 100 + (isIncrease ? percent : -percent);

  // Krok 1: równanie na cenę po zmianie
  const step1 = `x \\cdot \\left(1 ${sign} \\dfrac{${percent}}{100}\\right) = ${finalPrice}\\,\\text{zł}`;

  // Krok 2: zapis z ułamkiem zwykłym
  const step2 = `x \\cdot \\dfrac{${factorNum}}{100} = ${finalPrice}\\,\\text{zł}`;

  // Krok 3: rozwiązanie równania
  const step3 = `x = ${finalPrice} \\cdot \\dfrac{100}{${factorNum}} = ${base}\\,\\text{zł}`;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginTop: 12,
      }}
    >
      <p>
        <strong>Krok 1:</strong> Oznacz cenę przed zmianą przez{" "}
        <InlineMath math={"x"} /> i zapisz równanie na cenę po zmianie.
      </p>
      <BlockMath math={step1} />

      <p>
        <strong>Krok 2:</strong> Zapisz współczynnik zmiany w postaci ułamka.
      </p>
      <BlockMath math={step2} />

      <p>
        <strong>Krok 3:</strong> Rozwiąż równanie względem{" "}
        <InlineMath math={"x"} /> – to jest cena przed zmianą.
      </p>
      <BlockMath math={step3} />
    </div>
  );
}
